import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { MassOverwriteButton } from '@/common/entityFramework/entityPerspective/overwrite/MassOverwriteButton';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TheraSelect } from '../../../theraSelect/TheraSelect';

export const AbvPbmLiabPerspective = ({ overUnderScenId }) => {
  const entityPerspectiveRef = useRef();
  const theraSelectRef = useRef();

  const rsrc = useRsrc();
  const module = useModuleFromRoute();
  const [earnedPeriodIds, setEarnedPeriodIds] = useState(null);

  const entity = 'PBM_LIAB';

  const views = [
    {
      name: 'Over/(Under)',
      dims: ['OVER_UNDER_SCEN', 'EARNED_PERIOD', 'CHANNEL', 'PBM', 'PROD'],
      kpis: ['PYMT_AMT', 'PYMT_ADJ', 'TOTAL_PYMT', 'FORECASTED_LIAB', 'LIAB_ADJ', 'ACTIVITY', 'PROJ_PYMT_AMT'],
      disabledDims: ['OVER_UNDER_SCEN'],
      numFrozenDims: 1,
      dimToPeriodicities: {
        EARNED_PERIOD: ['LIFETIME', 'YEAR', 'QTR'],
      },
    },
  ];

  useEffect(() => {
    rsrc
      .data(module, 'OVER_UNDER_SCEN_VAL_QTR_DATA_PLUGIN', { entity, overUnderScenId }, entity)
      .then((res) => setEarnedPeriodIds(res));
  }, [overUnderScenId]);

  const handleViewReset = useCallback(() => {
    theraSelectRef.current?.reset();
  }, []);

  if (earnedPeriodIds === null) return null;

  const defaultDimFilters = { OVER_UNDER_SCEN: [overUnderScenId], EARNED_PERIOD: earnedPeriodIds };

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    const pymtItems = [
      {
        name: 'Payment Amounts',
        action: () =>
          entityPerspectiveRef.current.navigateToViewer(
            params,
            'PYMT_AMT',
            'Payment Amounts',
            'PBM_LIAB_PYMT_VIEWER_PLUGIN',
          ),
        icon: fas.faListUl,
      },
      {
        name: 'Payment Adjustments',
        action: () =>
          entityPerspectiveRef.current.navigateToViewer(
            params,
            'PYMT_ADJ',
            'Payment Adjustments',
            'PBM_LIAB_PYMT_ADJ_VIEWER_PLUGIN',
          ),
        icon: fas.faListUl,
      },
    ];

    contextMenus.push(
      {
        name: 'Payment Transaction Details',
        subMenu: pymtItems,
        icon: fas.faListUl,
      },
      {
        name: 'Forecasted Liability',
        action: () =>
          entityPerspectiveRef.current.navigateToPerspectiveAdj(params, 'FORECASTED_LIAB', 'Forecasted Liability'),
        icon: fas.faSlidersH,
      },
      {
        name: 'Liability Adjustments',
        action: () =>
          entityPerspectiveRef.current.navigateToPerspectiveAdj(params, 'LIAB_ADJ', 'Liability Adjustments'),
        icon: fas.faSlidersH,
      },
      {
        name: 'Export',
        action: () => entityPerspectiveRef.current.openExportDialog(params),
        icon: fas.faArrowCircleDown,
      },
    );

    return contextMenus;
  };

  const augButtons = (buttons) => {
    buttons.push(
      <TheraSelect entityPerspectiveRef={entityPerspectiveRef} ref={theraSelectRef} />,
      <MassOverwriteButton
        role={'GTN_FORECASTED_LIAB_SAVE'}
        toolTip={'Adjust Forecasted Liability'}
        kpiCodes={['FORECASTED_LIAB']}
        entity={entity}
        defaultDimValues={{ OVER_UNDER_SCEN: overUnderScenId }}
        augDimFieldDef={(fieldDef, { dimCode, baseExpr }) => {
          if (dimCode === 'CHANNEL') {
            fieldDef.expr = ExprUtils.and(baseExpr, ExprUtils.setExpr('code', ['MCO', 'MPD']));
          }
        }}
      />,
    );
    buttons.push(
      <MassOverwriteButton
        role={'GTN_LIAB_ADJ_SAVE'}
        toolTip={'Liability Adjustment'}
        kpiCodes={['LIAB_ADJ']}
        entity={entity}
        defaultDimValues={{ OVER_UNDER_SCEN: overUnderScenId }}
        augDimFieldDef={(fieldDef, { dimCode, baseExpr }) => {
          if (dimCode === 'CHANNEL') {
            fieldDef.expr = ExprUtils.and(baseExpr, ExprUtils.setExpr('code', ['MCO', 'MPD']));
          }
        }}
      />,
      <TitleButtonDivider key="filterButtonDivider" />,
    );
  };

  const events = {
    onDimFilterChange: ({ dimCode }) => {
      if (dimCode === 'PROD') {
        theraSelectRef.current.reset();
      }
    },
  };

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      defaultDimFilters={defaultDimFilters}
      augButtons={augButtons}
      onViewReset={handleViewReset}
      events={events}
      entity={entity}
      gridOptions={{
        getContextMenuItems,
      }}
    />
  );
};
